import React, { Component } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  FormGroup,
} from "reactstrap";
import moment from "moment";
import Swal from "sweetalert2";
import Select from "react-select";
import { withNamespaces } from "react-i18next";
import { ActivityDatabaseConnector } from "../../dataconnector/activityDatabaseConnector";
import { SiteDatabaseConnector } from "../../dataconnector/siteDatabaseConnector";
import { AnimalDatabaseConnector } from "../../dataconnector/animalDatabaseConnector";
import { cachedFetch } from "../../utils/cache/refDataCache";
import { getZooId } from "../../tools/conf";
import { ACTIVITY_TYPES, DEFAULT_ACTIVITY_TYPE } from "./activityTypes";
import { readActivityQuickAddNavigationState } from "./activityQuickAddNavigationState";

const activityConnect = new ActivityDatabaseConnector();
const siteConnect = new SiteDatabaseConnector();
const animalConnect = new AnimalDatabaseConnector();

const DATE_CHIPS = [
  "today",
  "tomorrow",
  "endOfWeek",
  "endOfMonth",
  "endOfQuarter",
  "endOfYear",
  "custom",
];

// Entity types an activity can point to, with their detail-view URL
// (query-param form, so the link works in a new tab too)
const ENTITY_TYPES = ["sector", "enclosure", "animal"];
const ENTITY_ACTION_URLS = {
  sector: (id) => `/SectorManagementView?sectorId=${id}`,
  enclosure: (id) => `/EnclosureManagementView?enclosureId=${id}`,
  animal: (id) => `/AnimalManagementView?animalId=${id}`,
};

/**
 * Quick-add modal for personal activities ("Me créer un rappel").
 * One screen: title + due date chips + optional note. Self-assigned by
 * definition — no sector, no responsibles, no recurrence. The advanced
 * 3-step TaskCreationModal remains the tool for team tasks.
 *
 * Props:
 * - isOpen, onClose
 * - activity: existing activity to edit (optional; create mode otherwise)
 * - defaultTitle / defaultNote: prefill for create mode (optional)
 * - linkedEntity: {type, id, name, actionUrl} attached on create (optional)
 * - sourceRef: {type, id} provenance attached on create (optional)
 * - onSaved: callback after a successful create/update (optional)
 */
class ActivityQuickAddModal extends Component {
  state = this.getInitialState();
  navigationBaseline = this.state;

  publishNavigationState = () => {
    this.props.onNavigationStateChange?.(
      readActivityQuickAddNavigationState({
        baseline: this.navigationBaseline,
        current: this.state,
        open: this.props.isOpen,
      })
    );
  };

  getInitialState() {
    const { activity, defaultTitle, defaultNote } = this.props;
    const existingEntity = activity ? activity.linkedEntity : null;
    const isPickableEntity =
      existingEntity && ENTITY_TYPES.includes(existingEntity.type);

    const entityState = {
      entityType: isPickableEntity ? existingEntity.type : null,
      entityId: isPickableEntity ? existingEntity.id : null,
      entityName: isPickableEntity ? existingEntity.name : null,
      entityOptions: { sector: null, enclosure: null, animal: null },
      loadingEntities: false,
    };

    if (activity) {
      const due = activity.dueAt && activity.dueAt.toDate
        ? moment(activity.dueAt.toDate())
        : moment.unix(activity.dueAtUnix || moment().unix());
      return {
        title: activity.title || "",
        note: activity.note || "",
        activityType: activity.activityType || DEFAULT_ACTIVITY_TYPE,
        dateChoice: "custom",
        customDate: due.format("YYYY-MM-DD"),
        time: due.format("HH:mm"),
        saving: false,
        ...entityState,
      };
    }
    return {
      title: defaultTitle || "",
      note: defaultNote || "",
      activityType: this.props.defaultActivityType || DEFAULT_ACTIVITY_TYPE,
      dateChoice: "today",
      customDate: moment().format("YYYY-MM-DD"),
      time: this.getDefaultTime(),
      saving: false,
      ...entityState,
    };
  }

  // Default to the next full hour so a "today" reminder is never already due
  getDefaultTime() {
    const next = moment().add(1, "hour").startOf("hour");
    return next.isSame(moment(), "day") ? next.format("HH:mm") : "09:00";
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.setState(this.getInitialState(), () => {
        this.navigationBaseline = this.state;
        this.publishNavigationState();
        if (this.state.entityType) this.loadEntityOptions(this.state.entityType);
      });
      return;
    }
    if (!this.props.isOpen && prevProps.isOpen) {
      this.publishNavigationState();
      return;
    }
    if (
      prevState !== this.state ||
      prevProps.onNavigationStateChange !== this.props.onNavigationStateChange
    ) {
      this.publishNavigationState();
    }
  }

  componentDidMount() {
    this.navigationBaseline = this.state;
    this.publishNavigationState();
    if (this.props.isOpen && this.state.entityType) {
      this.loadEntityOptions(this.state.entityType);
    }
  }

  componentWillUnmount() {
    this.props.onNavigationStateChange?.({ dirty: false, pending: false });
  }

  // Lazy-load the pickable entities of a type (zoo-wide lists, shared cache)
  loadEntityOptions = async (type) => {
    if (this.state.entityOptions[type]) return;
    this.setState({ loadingEntities: true });
    try {
      const zooId = await getZooId();
      let options = [];
      if (type === "sector") {
        const sectors = await cachedFetch(`sectors:${zooId}`, () =>
          siteConnect._getSectorIndexFromZooId(zooId)
        );
        options = (sectors || [])
          .filter((s) => !s.isArchived)
          .map((s) => ({ value: s.sectorId || s.value, label: s.sectorName || s.label }));
      } else if (type === "enclosure") {
        const enclosures = await cachedFetch(`enclosures:${zooId}`, () =>
          siteConnect._getEnclosuresList()
        );
        options = (enclosures || []).map((e) => ({
          value: e.enclosureId,
          label: e.sectorName ? `${e.enclosureName} (${e.sectorName})` : e.enclosureName,
        }));
      } else if (type === "animal") {
        const animals = await cachedFetch(`animals:${zooId}`, () =>
          animalConnect._getAnimalsList(zooId)
        );
        options = (animals || []).map((a) => ({
          value: a.value,
          label: a.specieName ? `${a.label} (${a.specieName})` : a.label,
        }));
      }
      options.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
      this.setState((prev) => ({
        entityOptions: { ...prev.entityOptions, [type]: options },
        loadingEntities: false,
      }));
    } catch (error) {
      console.error("[ActivityQuickAdd] Error loading entities:", error);
      this.setState({ loadingEntities: false });
    }
  };

  selectEntityType = (type) => {
    if (this.state.entityType === type) {
      // Toggle off: detach the entity
      this.setState({ entityType: null, entityId: null, entityName: null });
      return;
    }
    this.setState({ entityType: type, entityId: null, entityName: null });
    this.loadEntityOptions(type);
  };

  getDueMoment = () => {
    const { dateChoice, customDate, time } = this.state;
    let day;
    if (dateChoice === "today") day = moment();
    else if (dateChoice === "tomorrow") day = moment().add(1, "day");
    else if (dateChoice === "endOfWeek") day = moment().endOf("isoWeek");
    else if (dateChoice === "endOfMonth") day = moment().endOf("month");
    else if (dateChoice === "endOfQuarter") day = moment().endOf("quarter");
    else if (dateChoice === "endOfYear") day = moment().endOf("year");
    else day = moment(customDate, "YYYY-MM-DD");

    const [hours, minutes] = (time || "09:00").split(":");
    return day
      .clone()
      .set({ hour: parseInt(hours, 10) || 9, minute: parseInt(minutes, 10) || 0, second: 0, millisecond: 0 });
  };

  // Linked entity resolved from the picker (null = none selected)
  getPickedEntity = () => {
    const { entityType, entityId, entityName, entityOptions } = this.state;
    if (!entityType || !entityId) return null;
    const option = (entityOptions[entityType] || []).find((o) => o.value === entityId);
    return {
      type: entityType,
      id: entityId,
      name: (option && option.label) || entityName || "",
      actionUrl: ENTITY_ACTION_URLS[entityType](entityId),
    };
  };

  handleSave = async () => {
    const { t, activity, linkedEntity, sourceRef, onSaved, onClose } = this.props;
    const title = this.state.title.trim();
    if (!title) return;

    const dueMoment = this.getDueMoment();
    if (!dueMoment.isValid()) return;

    this.setState({ saving: true });

    const pickedEntity = this.getPickedEntity();

    let result;
    if (activity) {
      const originalWasPickable =
        activity.linkedEntity && ENTITY_TYPES.includes(activity.linkedEntity.type);
      result = await activityConnect._updateActivity(activity.activityId || activity.id, {
        title,
        note: this.state.note,
        activityType: this.state.activityType,
        dueAt: dueMoment.toDate(),
        // Detach only if the user removed a picker-managed entity;
        // a note link (from a notification) is never touched here
        linkedEntity: pickedEntity || (originalWasPickable ? null : undefined),
      });
    } else {
      result = await activityConnect._createActivity({
        title,
        note: this.state.note,
        activityType: this.state.activityType,
        dueAt: dueMoment.toDate(),
        linkedEntity: pickedEntity || linkedEntity || null,
        sourceRef: sourceRef || null,
      });
    }

    this.setState({ saving: false });

    if (result.success) {
      this.navigationBaseline = this.state;
      this.props.onNavigationStateChange?.({ dirty: false, pending: false });
      // Ask for browser-notification permission at the most meaningful
      // moment: the user just scheduled a reminder for themselves
      if (typeof Notification !== "undefined" && Notification.permission === "default") {
        Notification.requestPermission();
      }
      if (onSaved) onSaved(result);
      onClose({ persisted: true });
    } else {
      Swal.fire(t("common.error"), t("activities.swal.error"), "error");
    }
  };

  renderDateChip = (chip) => {
    const { t } = this.props;
    const isActive = this.state.dateChoice === chip;
    return (
      <Button
        key={chip}
        size="sm"
        color={isActive ? "primary" : "secondary"}
        outline={!isActive}
        className="mr-2 mb-2"
        style={{ borderRadius: "20px" }}
        onClick={() => this.setState({ dateChoice: chip })}
      >
        {t(`activities.date.${chip}`)}
      </Button>
    );
  };

  renderEntityPicker = () => {
    const { t } = this.props;
    const { entityType, entityId, entityName, entityOptions, loadingEntities } = this.state;
    const options = entityType ? entityOptions[entityType] || [] : [];
    const selectedOption =
      entityType && entityId
        ? options.find((o) => o.value === entityId) ||
          (entityName ? { value: entityId, label: entityName } : null)
        : null;

    return (
      <FormGroup className="mb-0 mt-3">
        <Label style={{ fontWeight: 600, color: "#5a5c69", fontSize: "13px" }}>
          {t("activities.picker.label")}
        </Label>
        <div className="d-flex flex-wrap">
          {ENTITY_TYPES.map((type) => {
            const isActive = entityType === type;
            return (
              <Button
                key={type}
                size="sm"
                color={isActive ? "primary" : "secondary"}
                outline={!isActive}
                className="mr-2 mb-2"
                style={{ borderRadius: "20px" }}
                onClick={() => this.selectEntityType(type)}
              >
                {t(`activities.picker.${type}`)}
              </Button>
            );
          })}
        </div>
        {entityType && (
          <Select
            options={options}
            value={selectedOption}
            isLoading={loadingEntities}
            isClearable
            placeholder={t(`activities.picker.${entityType}Placeholder`)}
            noOptionsMessage={() => t("activities.picker.noOptions")}
            onChange={(option) =>
              this.setState({
                entityId: option ? option.value : null,
                entityName: option ? option.label : null,
              })
            }
          />
        )}
      </FormGroup>
    );
  };

  render() {
    const { t, isOpen, onClose, activity, linkedEntity } = this.props;
    const { title, note, dateChoice, customDate, time, saving } = this.state;
    const dueMoment = this.getDueMoment();
    // The read-only info box is reserved for non-pickable links
    // (e.g. a note attached via "Me le rappeler" on a notification)
    const rawEntity = activity ? activity.linkedEntity : linkedEntity;
    const entity = rawEntity && !ENTITY_TYPES.includes(rawEntity.type) ? rawEntity : null;

    return (
      <Modal isOpen={isOpen} toggle={onClose} size="md">
        <ModalHeader
          toggle={onClose}
          close={
            <button className="close" onClick={onClose} aria-label="Close">
              <span aria-hidden="true" style={{ fontSize: "1.5rem" }}>&times;</span>
            </button>
          }
          style={{
            backgroundColor: "#f8f9fc",
            borderBottom: "1px solid #e3e6f0",
          }}
        >
          <span style={{ color: "#5a5c69", fontWeight: 600 }}>
            <i className="fas fa-stopwatch mr-2"></i>
            {activity ? t("activities.quickAdd.editTitle") : t("activities.quickAdd.title")}
          </span>
        </ModalHeader>

        <ModalBody style={{ backgroundColor: "white", padding: "1.5rem" }}>
          <FormGroup>
            <Label style={{ fontWeight: 600, color: "#5a5c69", fontSize: "13px" }}>
              {t("activities.form.typeLabel")}
            </Label>
            <div className="d-flex flex-wrap">
              {ACTIVITY_TYPES.map((type) => {
                const isActive = this.state.activityType === type.key;
                return (
                  <Button
                    key={type.key}
                    size="sm"
                    color={isActive ? "primary" : "secondary"}
                    outline={!isActive}
                    className="mr-2 mb-2"
                    style={{ borderRadius: "20px" }}
                    onClick={() => this.setState({ activityType: type.key })}
                  >
                    <i className={`fas ${type.icon} mr-1`}></i>
                    {t(`activities.types.${type.key}`)}
                  </Button>
                );
              })}
            </div>
          </FormGroup>

          <FormGroup>
            <Label style={{ fontWeight: 600, color: "#5a5c69", fontSize: "13px" }}>
              {t("activities.form.titleLabel")}
            </Label>
            <Input
              type="text"
              value={title}
              autoFocus
              placeholder={t("activities.form.titlePlaceholder")}
              onChange={(e) => this.setState({ title: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") this.handleSave();
              }}
            />
          </FormGroup>

          <FormGroup>
            <Label style={{ fontWeight: 600, color: "#5a5c69", fontSize: "13px" }}>
              {t("activities.form.dueLabel")}
            </Label>
            <div className="d-flex flex-wrap">
              {DATE_CHIPS.map((chip) => this.renderDateChip(chip))}
            </div>
            <div className="d-flex mt-1" style={{ gap: 10 }}>
              {dateChoice === "custom" && (
                <Input
                  type="date"
                  value={customDate}
                  style={{ maxWidth: 180 }}
                  onChange={(e) => this.setState({ customDate: e.target.value })}
                />
              )}
              <Input
                type="time"
                value={time}
                style={{ maxWidth: 130 }}
                onChange={(e) => this.setState({ time: e.target.value })}
              />
            </div>
            {dueMoment.isValid() && (
              <small className="text-muted d-block mt-1">
                <i className="far fa-clock mr-1"></i>
                {dueMoment.format("DD/MM/YYYY HH:mm")}
              </small>
            )}
          </FormGroup>

          <FormGroup className="mb-0">
            <Label style={{ fontWeight: 600, color: "#5a5c69", fontSize: "13px" }}>
              {t("activities.form.noteLabel")}
            </Label>
            <Input
              type="textarea"
              rows={2}
              value={note}
              placeholder={t("activities.form.notePlaceholder")}
              onChange={(e) => this.setState({ note: e.target.value })}
            />
          </FormGroup>

          {entity && entity.name ? (
            <div
              className="mt-3 px-3 py-2"
              style={{
                backgroundColor: "#f8f9fc",
                border: "1px solid #e3e6f0",
                borderRadius: "8px",
                fontSize: "13px",
                color: "#5a5c69",
              }}
            >
              <i className="fas fa-link mr-2"></i>
              {t("activities.form.linkedTo")} <strong>{entity.name}</strong>
            </div>
          ) : (
            this.renderEntityPicker()
          )}
        </ModalBody>

        <ModalFooter style={{ borderTop: "1px solid #e3e6f0" }}>
          <Button color="secondary" outline onClick={onClose} disabled={saving}>
            {t("common.cancel")}
          </Button>
          <Button
            color="primary"
            onClick={this.handleSave}
            disabled={saving || !title.trim() || !dueMoment.isValid()}
          >
            <i className={`fas fa-${activity ? "save" : "plus-circle"} mr-1`}></i>
            {activity ? t("activities.form.save") : t("activities.form.create")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

export default withNamespaces("translations")(ActivityQuickAddModal);
